import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('MoneyInput')
class Money {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('money') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => Float)
  @Prop({ type: Number })
  amount: number

  @Field(() => String)
  @Prop({ type: String })
  currency: string
}

@ObjectType()
@InputType('RateDetailInput')
class RateDetail {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('ratedetail') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => String)
  @Prop({ type: String })
  rate_detail_type: string

  @Field(() => String)
  @Prop({ type: String })
  carrier_description: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  carrier_billing_code?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  carrier_memo?: string

  @Field(() => Money)
  @Prop({ type: Money })
  amount: Money

  @Field(() => String)
  @Prop({ type: String })
  billing_source: string
}

@ObjectType()
@InputType('LabelDownloadInput')
class LabelDownload {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('lbldwn') })
  _id: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  pdf?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  png?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  zpl?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  href?: string
}

@ObjectType()
@InputType('FormDownloadInput')
class FormDownload {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('frmdwn') })
  _id: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  href?: string
}

@ObjectType()
@InputType('PackageLabelMessagesInput')
class PackageLabelMessages {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('pkglbl') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  reference1?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  reference2?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  reference3?: string
}

@ObjectType()
@InputType('ShipmentPackageInput')
class ShipmentPackage {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('shppkg') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => String)
  @Prop({ type: Number })
  package_id: number

  @Field(() => String)
  @Prop({ type: String })
  package_code: string

  @Field(() => Float)
  @Prop({ type: Number })
  weight_value: number

  @Field(() => String)
  @Prop({ type: String })
  weight_unit: string

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  length: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  width: number

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  height: number

  @Field(() => Money, { nullable: true })
  @Prop({ type: Money, nullable: true })
  insured_value?: Money

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  tracking_number?: string

  @Field(() => LabelDownload, { nullable: true })
  @Prop({ type: LabelDownload, nullable: true })
  label_download?: LabelDownload

  @Field(() => PackageLabelMessages, { nullable: true })
  @Prop({ type: PackageLabelMessages, nullable: true })
  label_messages?: PackageLabelMessages

  @Field(() => Number, { nullable: true })
  @Prop({ type: Number })
  sequence?: number

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_label_documents: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_form_documents: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_qr_code_documents: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  has_paperless_label_documents: boolean
}

@ObjectType()
@InputType('ShipmentAddressInput')
class ShipmentAddress {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('shpaddr') })
  _id: string

  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => String)
  @Prop({ type: String })
  name: string

  @Field(() => String)
  @Prop({ type: String })
  phone: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  email?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  company_name?: string

  @Field(() => String)
  @Prop({ type: String })
  address_line1: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  address_line2?: string

  @Field(() => String)
  @Prop({ type: String })
  city_locality: string

  @Field(() => String)
  @Prop({ type: String })
  state_province: string

  @Field(() => String)
  @Prop({ type: String })
  postal_code: string

  @Field(() => String)
  @Prop({ type: String })
  country_code: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  address_residential_indicator?: string
}

@ObjectType()
@InputType('ShipEngineInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class ShipEngine extends BaseEntity {
  @Field(() => String)
  @Prop({ type: String })
  label_id: string

  @Field(() => String)
  @Prop({ type: String })
  shipment_id: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  external_shipment_id?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  external_order_id?: string

  @Field(() => Date)
  @Prop({ type: Date })
  ship_date: Date

  @Field(() => Date)
  @Prop({ type: Date })
  created_at: Date

  @Field(() => Money)
  @Prop({ type: Money })
  shipment_cost: Money

  @Field(() => Money)
  @Prop({ type: Money })
  insurance_cost: Money

  @Field(() => [RateDetail])
  @Prop({ type: [RateDetail], default: [] }, PropType.ARRAY)
  rate_details: RateDetail[]

  @Field(() => String)
  @Prop({ type: String })
  tracking_number: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  is_return_label: boolean

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  is_international: boolean

  @Field(() => String)
  @Prop({ type: String })
  batch_id: string

  @Field(() => String)
  @Prop({ type: String })
  carrier_id: string

  @Field(() => String)
  @Prop({ type: String })
  service_code: string

  @Field(() => String)
  @Prop({ type: String })
  package_code: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  voided: boolean

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  voided_at?: Date

  @Field(() => String)
  @Prop({ type: String })
  label_format: string

  @Field(() => String)
  @Prop({ type: String })
  label_layout: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  trackable: boolean

  @Field(() => String)
  @Prop({ type: String })
  carrier_code: string

  @Field(() => String)
  @Prop({ type: String })
  confirmation: string

  @Field(() => String)
  @Prop({ type: String })
  tracking_status: string

  @Field(() => LabelDownload, { nullable: true })
  @Prop({ type: LabelDownload, nullable: true })
  label_download?: LabelDownload

  @Field(() => [ShipmentPackage])
  @Prop({ type: [ShipmentPackage], default: [] }, PropType.ARRAY)
  packages: ShipmentPackage[]

  @Field(() => String)
  @Prop({ type: String })
  charge_event: string

  @Field(() => [String])
  @Prop({ type: [String], default: [] }, PropType.ARRAY)
  alternative_identifiers: string[]

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  shipping_rule_id?: string

  @Field(() => String)
  @Prop({ type: String })
  tracking_url: string

  @Field(() => ShipmentAddress)
  @Prop({ type: ShipmentAddress })
  ship_to: ShipmentAddress

  @Field(() => FormDownload)
  @Prop({ type: FormDownload })
  form_download: FormDownload
}
